import React, { useCallback, useEffect, useRef, useState } from "react";
import { inject, observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import { getTheme, toggleTheme, THEMES } from "../../lib/themeManager";
import { getConfig } from "../../lib/configMgr";
import { useReleaseNotesContext } from "./ReleaseNotesContext";
import AboutModal from "./AboutModal";

/**
 * AccountMenuBase — presentational v2 account dropdown, anchored to the
 * nav-bar avatar. A full mirror of the classic UserMenu (theme, API key, docs,
 * change password, sign out) restyled for v2, plus "What's New" at the top.
 * Open-state is local; all actions are injected via props.
 */
export const AccountMenuBase = ({
  userName,
  userGroups = [],
  isAdmin,
  apiKey,
  currentTheme,
  onOpenNotes,
  onStartTour,
  onShowWelcome,
  onToggleTheme,
  onGenerateAPIKey,
  onOpenDevDocs,
  onOpenAPIDocs,
  onOpenAbout,
  onChangePassword,
  onSignOut,
}) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Run an action then close the menu (the consistent select behavior).
  const pick = (fn) => () => { setOpen(false); fn?.(); };

  const initial = (userName || "?").slice(0, 1).toUpperCase();

  const copyKey = () => {
    if (!apiKey) return;
    try {
      navigator.clipboard?.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* ignore */ }
  };

  return (
    <div className="account" ref={rootRef}>
      <button
        type="button"
        className="workspace-navbar__avatar account__trigger"
        data-testid="v2-account-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        title={userName || "Account"}
        onClick={() => setOpen((v) => !v)}
      >
        {initial}
      </button>

      {open && (
        <div className="account__menu" data-testid="v2-account-menu" role="menu">
          <div className="account__header">
            <div className="account__hello">Hello, {userName || "anonymous"}!</div>
            {isAdmin && userGroups.length > 0 && (
              <div className="account__groups">Member of {userGroups.join(", ")}</div>
            )}
          </div>

          <div className="account__divider" role="separator" />

          <button
            type="button" role="menuitem"
            className="account__item account__item--accent"
            data-testid="v2-account-whatsnew"
            onClick={pick(onOpenNotes)}
          >
            <span className="account__icon" aria-hidden="true">✦</span>
            <span>What's New</span>
          </button>

          <button
            type="button" role="menuitem"
            className="account__item"
            data-testid="v2-account-tour"
            onClick={pick(onStartTour)}
          >
            <span className="account__icon" aria-hidden="true">🧭</span>
            <span>Take a tour</span>
          </button>

          <button
            type="button" role="menuitem"
            className="account__item"
            data-testid="v2-account-welcome"
            onClick={pick(onShowWelcome)}
          >
            <span className="account__icon" aria-hidden="true">👋</span>
            <span>Show welcome</span>
          </button>

          <div className="account__divider" role="separator" />

          <button
            type="button" role="menuitem"
            className="account__item"
            data-testid="v2-account-theme"
            onClick={pick(onToggleTheme)}
          >
            <span className="account__icon" aria-hidden="true">
              {currentTheme === THEMES.DARK ? "☀️" : "🌙"}
            </span>
            <span>{currentTheme === THEMES.DARK ? "Light Mode" : "Dark Mode"}</span>
          </button>

          <div className="account__divider" role="separator" />

          <div className="account__section-label">API Key</div>
          <button
            type="button" role="menuitem"
            className="account__item account__item--key"
            data-testid="v2-account-apikey"
            title="Current API Key — click to copy"
            disabled={!apiKey}
            onClick={copyKey}
          >
            <span className="account__icon" aria-hidden="true">⧉</span>
            <span className="account__key">{apiKey || "No key has been generated"}</span>
            {copied && <span className="account__copied">Copied!</span>}
          </button>
          <button
            type="button" role="menuitem"
            className="account__item"
            data-testid="v2-account-generate-key"
            onClick={pick(onGenerateAPIKey)}
          >
            <span className="account__icon" aria-hidden="true">🔑</span>
            <span>{apiKey ? "Regenerate" : "Generate"} API Key</span>
          </button>

          <div className="account__divider" role="separator" />

          <button
            type="button" role="menuitem"
            className="account__item"
            data-testid="v2-account-devdocs"
            onClick={pick(onOpenDevDocs)}
          >
            <span className="account__icon" aria-hidden="true">📘</span>
            <span>Developer Docs</span>
          </button>
          <button
            type="button" role="menuitem"
            className="account__item"
            data-testid="v2-account-apidocs"
            disabled={!apiKey}
            onClick={pick(onOpenAPIDocs)}
          >
            <span className="account__icon" aria-hidden="true">📗</span>
            <span>API Reference</span>
          </button>
          <button
            type="button" role="menuitem"
            className="account__item"
            data-testid="v2-account-about"
            onClick={pick(onOpenAbout)}
          >
            <span className="account__icon" aria-hidden="true">ℹ️</span>
            <span>About Plex</span>
          </button>

          <div className="account__divider" role="separator" />

          <button
            type="button" role="menuitem"
            className="account__item"
            data-testid="v2-account-changepass"
            onClick={pick(onChangePassword)}
          >
            <span className="account__icon" aria-hidden="true">🔒</span>
            <span>Change Password</span>
          </button>

          <div className="account__divider" role="separator" />

          <button
            type="button" role="menuitem"
            className="account__item account__item--danger"
            data-testid="v2-account-signout"
            onClick={pick(onSignOut)}
          >
            <span className="account__icon" aria-hidden="true">⎋</span>
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * AccountMenu — the injected/router-aware wrapper. Reuses the exact classic
 * UserMenu handler logic (theme, API key, docs, change-pass, SSO-aware sign-out)
 * and wires "What's New" to the release-notes context.
 */
const AccountMenuWrapper = ({ userStore, appStatusStore, searchSessionStore, tutorialStore }) => {
  const navigate = useNavigate();
  const releaseNotes = useReleaseNotesContext();
  const [currentTheme, setCurrentTheme] = useState(getTheme());
  const [aboutOpen, setAboutOpen] = useState(false);

  const { userName, userGroups, apiKey, isAdmin } = userStore;

  const onToggleTheme = useCallback(() => { toggleTheme(); setCurrentTheme(getTheme()); }, []);

  // Replay the workspace onboarding tour. The tour's real targets live on the
  // workspace home + nav bar, so route there first, then start once the home
  // has had a tick to mount (Joyride's per-step targetWaitTimeout absorbs the
  // rest). resetContext clears any prior completed/skipped state so it re-runs.
  const onStartTour = useCallback(() => {
    tutorialStore?.resetContext("v2-workspace");
    navigate("/v2");
    setTimeout(() => tutorialStore?.startContextTutorial("v2-workspace"), 350);
  }, [tutorialStore, navigate]);

  // Re-surface the one-time welcome banner: clear its localStorage flag, route
  // to the workspace home, and nudge it to remount even if we're already there.
  const onShowWelcome = useCallback(() => {
    try { window.localStorage.removeItem("workspace.welcome-seen"); } catch (_) { /* ignore */ }
    navigate("/v2");
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("workspace:show-welcome"));
    }
  }, [navigate]);
  const onGenerateAPIKey = useCallback(() => { userStore.generateAPIKey(); }, [userStore]);
  const onOpenDevDocs = useCallback(() => {
    window.open("/developer", "_blank", "noopener,noreferrer");
  }, []);
  const onOpenAPIDocs = useCallback(() => {
    userStore.refreshUICookie();
    const params = new URLSearchParams();
    if (currentTheme === "dark") params.set("theme", "dark");
    const qs = params.toString();
    window.open(`/api/ui/${qs ? "?" + qs : ""}`, "_blank", "noopener,noreferrer");
  }, [userStore, currentTheme]);
  const onChangePassword = useCallback(() => {
    navigate("/changepass", { state: { username: userName } });
  }, [navigate, userName]);
  const onSignOut = useCallback(() => {
    const wasSSOUser = userStore.isSSOUser;
    appStatusStore.clear();
    searchSessionStore.clear();
    userStore.logout();
    if (wasSSOUser) {
      const { AWSCognito: { authDomain, clientID } } = getConfig();
      const logoutUri = `${window.location.origin}/`;
      window.location.href = `https://${authDomain}/logout?client_id=${clientID}&logout_uri=${encodeURIComponent(logoutUri)}`;
    } else {
      window.location.href = "/signin";
    }
  }, [userStore, appStatusStore, searchSessionStore]);

  return (
    <>
      <AccountMenuBase
        userName={userName}
        userGroups={userGroups || []}
        isAdmin={isAdmin}
        apiKey={apiKey}
        currentTheme={currentTheme}
        onOpenNotes={() => releaseNotes?.openNotes?.()}
        onStartTour={onStartTour}
        onShowWelcome={onShowWelcome}
        onToggleTheme={onToggleTheme}
        onGenerateAPIKey={onGenerateAPIKey}
        onOpenDevDocs={onOpenDevDocs}
        onOpenAPIDocs={onOpenAPIDocs}
        onOpenAbout={() => setAboutOpen(true)}
        onChangePassword={onChangePassword}
        onSignOut={onSignOut}
      />
      <AboutModal
        open={aboutOpen}
        onClose={() => setAboutOpen(false)}
        versionRequirements={appStatusStore.versionRequirements}
        versionInfo={appStatusStore.versionInfo}
        isAdmin={isAdmin}
      />
    </>
  );
};

export const AccountMenu = inject(
  "userStore",
  "appStatusStore",
  "searchSessionStore",
  "tutorialStore",
)(observer(AccountMenuWrapper));

export default AccountMenu;
